import { IconButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import {
  ChevronDownLine,
  ChevronRightLine,
} from "@dropbox/dig-icons/dist/mjs/assets";
import type { SearchResult } from "@mirage/shared/search/search-result";
import { KeyCodes } from "@mirage/shared/util/constants";
import { useState } from "react";
import styles from "./BrandedTypesDisplay.module.css";

interface BrandedTypeCount {
  type: string;
  count: number;
}

interface BrandedTypesDisplayProps {
  results: SearchResult[];
  totalCount: number;
}

export const BrandedTypesDisplay = ({
  results,
  totalCount,
}: BrandedTypesDisplayProps) => {
  const [expanded, setExpanded] = useState(false);

  // Count branded types
  const brandedTypeCounts = results.reduce(
    (acc, result) => {
      const brandedType = result.brandedType;
      if (brandedType) {
        acc[brandedType] = (acc[brandedType] || 0) + 1;
      }
      return acc;
    },
    {} as Record<string, number>,
  );

  // Sort by count (descending) and convert to array
  const sortedBrandedTypes: BrandedTypeCount[] = Object.entries(
    brandedTypeCounts,
  )
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);

  if (sortedBrandedTypes.length === 0) {
    return null;
  }

  const topThree = sortedBrandedTypes.slice(0, 3);

  const handleToggleAccordion = () => {
    setExpanded((prev) => !prev);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === KeyCodes.enter || event.key === KeyCodes.space) {
      event.preventDefault();
      handleToggleAccordion();
    }
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.header}
        onClick={handleToggleAccordion}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label="Toggle branded types breakdown"
      >
        <Text size="small" isBold>
          📊 Branded Types from {totalCount} results
        </Text>
        <div>
          <Text size="small" color="subtle">
            Top:{" "}
            {topThree.map(({ type, count }) => `${type} (${count})`).join(", ")}
          </Text>
          <IconButton
            variant="transparent"
            size="small"
            color="subtle"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleToggleAccordion();
            }}
            className={styles.chevronButton}
          >
            <UIIcon src={expanded ? ChevronDownLine : ChevronRightLine} />
          </IconButton>
        </div>
      </div>
      {expanded && (
        <div className={styles.expandedContainer}>
          {sortedBrandedTypes.map(({ type, count }) => (
            <div key={type} className={styles.brandedTypeItem}>
              <Text size="small">{type}</Text>
              <Text size="small" className={styles.count}>
                {count}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
